package com.fantasytenis.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.ThemeMode
import com.fantasytenis.web.theme.ThemeState
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Theme toggle button component.
 * Allows users to switch between light, dark, and system themes.
 */
@Composable
fun ThemeToggle() {
    val isDark by ThemeState.isDarkMode
    val themeMode by ThemeState.themeMode

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            padding(8.px, 12.px)
            borderRadius(20.px)
            backgroundColor(if (isDark) Colors.Gray800 else Colors.Gray100)
            cursor("pointer")
            property("user-select", "none")
            property("transition", "background-color 0.2s")
        }
        onClick {
            ThemeState.toggleTheme()
        }
    }) {
        // Sun icon
        Span({
            style {
                fontSize(16.px)
                opacity(if (isDark) 0.5 else 1.0)
                property("transition", "opacity 0.2s")
            }
        }) {
            Text("\u2600") // Sun symbol
        }

        // Toggle track
        Div({
            style {
                width(36.px)
                height(20.px)
                borderRadius(10.px)
                backgroundColor(if (isDark) Colors.Primary else Colors.Gray300)
                position(Position.Relative)
                property("transition", "background-color 0.2s")
            }
        }) {
            // Toggle thumb
            Div({
                style {
                    width(16.px)
                    height(16.px)
                    borderRadius(50.percent)
                    backgroundColor(Colors.White)
                    position(Position.Absolute)
                    top(2.px)
                    left(if (isDark) 18.px else 2.px)
                    property("transition", "left 0.2s")
                    property("box-shadow", "0 1px 3px rgba(0, 0, 0, 0.2)")
                }
            })
        }

        // Moon icon
        Span({
            style {
                fontSize(16.px)
                opacity(if (isDark) 1.0 else 0.5)
                property("transition", "opacity 0.2s")
            }
        }) {
            Text("\uD83C\uDF19") // Moon symbol
        }
    }
}

/**
 * Compact theme toggle (just icon, for tight spaces).
 */
@Composable
fun ThemeToggleIcon() {
    val isDark by ThemeState.isDarkMode

    Div({
        style {
            width(36.px)
            height(36.px)
            borderRadius(50.percent)
            backgroundColor(if (isDark) Colors.Gray800 else Colors.Gray100)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            cursor("pointer")
            property("transition", "background-color 0.2s, transform 0.2s")
        }
        onClick {
            ThemeState.toggleTheme()
        }
    }) {
        Span({
            style {
                fontSize(18.px)
                property("transition", "transform 0.3s")
            }
        }) {
            Text(if (isDark) "\u2600" else "\uD83C\uDF19")
        }
    }
}

/**
 * Theme selector dropdown with Light/Dark/System options.
 */
@Composable
fun ThemeSelector() {
    val themeMode by ThemeState.themeMode
    val isDark by ThemeState.isDarkMode

    Div({
        style {
            display(DisplayStyle.Flex)
            gap(4.px)
            padding(4.px)
            borderRadius(8.px)
            backgroundColor(if (isDark) Colors.Gray800 else Colors.Gray100)
        }
    }) {
        ThemeSelectorOption(
            label = "\u2600",
            tooltip = "Light",
            isSelected = themeMode == ThemeMode.LIGHT,
            isDark = isDark,
            onClick = { ThemeState.setTheme(ThemeMode.LIGHT) }
        )
        ThemeSelectorOption(
            label = "\uD83C\uDF19",
            tooltip = "Dark",
            isSelected = themeMode == ThemeMode.DARK,
            isDark = isDark,
            onClick = { ThemeState.setTheme(ThemeMode.DARK) }
        )
        ThemeSelectorOption(
            label = "\uD83D\uDCBB",
            tooltip = "System",
            isSelected = themeMode == ThemeMode.SYSTEM,
            isDark = isDark,
            onClick = { ThemeState.setTheme(ThemeMode.SYSTEM) }
        )
    }
}

@Composable
private fun ThemeSelectorOption(
    label: String,
    tooltip: String,
    isSelected: Boolean,
    isDark: Boolean,
    onClick: () -> Unit
) {
    Div({
        attr("title", tooltip)
        style {
            padding(8.px, 12.px)
            borderRadius(6.px)
            cursor("pointer")
            fontSize(14.px)
            backgroundColor(
                if (isSelected) {
                    if (isDark) Colors.Gray700 else Colors.White
                } else {
                    Color("transparent")
                }
            )
            property("transition", "background-color 0.2s")
            if (isSelected) {
                property("box-shadow", "0 1px 2px rgba(0, 0, 0, 0.1)")
            }
        }
        onClick { onClick() }
    }) {
        Text(label)
    }
}
